import { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Paper, Select, Spinner, Alert, Chip, Tabs, EmptyState } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import {
  adminApiClient,
  AdminApiError,
  type StoreRequestItem,
  type StoreRequestSummaryRow,
  type StoreRequestStatus,
} from '../lib/adminApiClient';

/**
 * Store-request demand board (storereq:read).
 * - "Demand" tab: requested stores ranked by how many users asked for them.
 * - "Requests" tab: the individual requests, filterable by status, with inline
 *   status triage for admins (storereq:write) — each change is audited server-side.
 */

const STATUSES: StoreRequestStatus[] = ['new', 'reviewing', 'fulfilled', 'declined'];
const STATUS_COLOR: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
  new: 'warning',
  reviewing: 'primary',
  fulfilled: 'success',
  declined: 'error',
};

const FILTER_OPTIONS = [
  { value: '', label: 'All statuses' },
  ...STATUSES.map((s) => ({ value: s, label: s })),
];

export default function StoreRequests() {
  const caps = useCapabilities();
  const canRead = caps.has('storereq:read');
  const canWrite = caps.has('storereq:write');

  const [view, setView] = useState<'summary' | 'list'>('summary');
  const [statusFilter, setStatusFilter] = useState<'' | StoreRequestStatus>('');
  const [summary, setSummary] = useState<StoreRequestSummaryRow[]>([]);
  const [requests, setRequests] = useState<StoreRequestItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const status = statusFilter || undefined;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (view === 'summary') {
        const res = await adminApiClient.storeRequestSummary(status);
        setSummary(res.items);
      } else {
        const res = await adminApiClient.listStoreRequests(status);
        setRequests(res.items);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load store requests');
    } finally {
      setLoading(false);
    }
  }, [view, status]);

  useEffect(() => {
    if (canRead) void load();
  }, [canRead, load]);

  if (caps.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }
  if (!canRead) {
    return <Alert severity="error">You don’t have permission to view store requests.</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Store requests
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Stores users have asked us to add.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Tabs
          ariaLabel="Store request view"
          value={view}
          onChange={(_e, v) => setView(v as 'summary' | 'list')}
          tabs={[
            { value: 'summary', label: 'Demand' },
            { value: 'list', label: 'Requests' },
          ]}
        />
        <Box sx={{ flex: 1 }} />
        <Select
          ariaLabel="Filter by status"
          label="Status"
          options={FILTER_OPTIONS}
          value={statusFilter}
          onChange={(_e, v) => setStatusFilter(v as '' | StoreRequestStatus)}
          size="small"
          native
          sx={{ width: 180 }}
        />
      </Box>

      {flash && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={flash.kind === 'success' ? 'success' : 'error'}>{flash.msg}</Alert>
        </Box>
      )}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Spinner color="brand" />
        </Box>
      ) : view === 'summary' ? (
        <SummaryView rows={summary} />
      ) : (
        <ListView
          rows={requests}
          canWrite={canWrite}
          onDone={(kind, msg) => {
            setFlash({ kind, msg });
            void load();
          }}
        />
      )}
    </Box>
  );
}

function storeLabel(chainName: string | null, specificStoreName: string | null): string {
  return chainName ?? specificStoreName ?? '(unnamed store)';
}

function SummaryView({ rows }: { rows: StoreRequestSummaryRow[] }) {
  if (rows.length === 0) {
    return <EmptyState icon="storefront" title="No requests yet" body="No store requests match this filter." />;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {rows.map((row, i) => (
        <Paper key={`${row.chainId ?? ''}-${row.specificStoreName ?? ''}-${i}`} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, width: 32, color: 'text.secondary' }}>
            {i + 1}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
            {storeLabel(row.chainName, row.specificStoreName)}
          </Typography>
          <Chip label={`${row.count} request${row.count === 1 ? '' : 's'}`} color="primary" size="small" />
        </Paper>
      ))}
    </Box>
  );
}

function ListView({
  rows,
  canWrite,
  onDone,
}: {
  rows: StoreRequestItem[];
  canWrite: boolean;
  onDone: (kind: 'success' | 'error', msg: string) => void;
}) {
  if (rows.length === 0) {
    return <EmptyState icon="storefront" title="No requests" body="No store requests match this filter." />;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {rows.map((row) => (
        <RequestRow key={row.requestId} row={row} canWrite={canWrite} onDone={onDone} />
      ))}
    </Box>
  );
}

function RequestRow({
  row,
  canWrite,
  onDone,
}: {
  row: StoreRequestItem;
  canWrite: boolean;
  onDone: (kind: 'success' | 'error', msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);

  const changeStatus = async (next: StoreRequestStatus) => {
    if (next === row.status) return;
    setBusy(true);
    try {
      await adminApiClient.updateStoreRequestStatus(row.requestId, next);
      onDone('success', `Marked “${storeLabel(row.chainName, row.specificStoreName)}” as ${next}.`);
    } catch (err) {
      const msg = err instanceof AdminApiError ? err.message : 'Failed to update status';
      onDone('error', msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {storeLabel(row.chainName, row.specificStoreName)}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {[row.specificStoreAddress, row.postalCode].filter(Boolean).join(' · ') || '—'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {row.sourceType && <Chip label={row.sourceType} size="small" />}
            {row.notifyEmail && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {row.notifyEmail}
              </Typography>
            )}
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {new Date(row.requestedAt).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={row.status} color={STATUS_COLOR[row.status] ?? 'default'} size="small" />
          {canWrite && (
            <Select
              ariaLabel="Change request status"
              options={STATUSES.map((s) => ({ value: s, label: s }))}
              value={STATUSES.includes(row.status as StoreRequestStatus) ? row.status : ''}
              onChange={(_e, v) => void changeStatus(v as StoreRequestStatus)}
              size="small"
              native
              disabled={busy}
              sx={{ width: 150 }}
            />
          )}
        </Box>
      </Box>
    </Paper>
  );
}
